import {
  chargeOrderNumber,
  normaliseQuote,
  rmbAmountParam,
  type ChinaPaymentType,
  type LegacyList,
  type Quote,
} from '@mypoz/shared';

import { isUnconfirmedFailure } from '@/features/packages/dictionary.js';
import { legacyApi } from '@/lib/legacy-api.js';

/** Raised when a China Payment endpoint refuses. The vendor's text is for the log, not the screen. */
export class ChinaPaymentRequestError extends Error {
  constructor(
    readonly endpoint: string,
    readonly code: string,
    message: string,
  ) {
    super(`${endpoint} failed (${code}): ${message}`);
    this.name = 'ChinaPaymentRequestError';
  }
}

/**
 * `currencyRate` for RMB (MYP-59, MYP-111) — the rate table behind the rate card, as the raw
 * `row` for `normaliseRateCard` in `@mypoz/shared` to read.
 *
 * POST with `currency=RMB`, checked live 2026-10-02: `{code: "1", row: {…}}`. A currency with no
 * table answers `row: null` (resolved here as `null`, and read as "no rates"); GET answers `-2`.
 * Read-only.
 */
export async function fetchRateRow(): Promise<unknown> {
  const result = await legacyApi.call<Readonly<Record<string, unknown>>>('currencyRate', {
    currency: 'RMB',
  });
  if (!result.ok) throw new ChinaPaymentRequestError('currencyRate', result.code, result.message);
  return result.data['row'] ?? null;
}

/**
 * `alipay_rechange` (MYP-60, MYP-108) — the live quote for an RMB amount, as a `Quote`.
 *
 * Always asked from the RMB side (`amt`, never `rm`: quoting from RM gives inconsistent fees,
 * MYP-64) and always with `currency=RMB` (anything else has no rate table). Read-only — a
 * calculation, nothing is recorded. **Throws** on a refusal (`code -1`, "the exchange rate result
 * is empty") or a reply without a rate or total.
 */
export async function fetchQuote(type: ChinaPaymentType, rmbCents: number): Promise<Quote> {
  const result = await legacyApi.call<Readonly<Record<string, unknown>>>('alipay_rechange', {
    amt: rmbAmountParam(rmbCents),
    type,
    currency: 'RMB',
  });
  if (!result.ok) {
    throw new ChinaPaymentRequestError('alipay_rechange', result.code, result.message);
  }
  const quote = normaliseQuote(type, rmbCents, result.data);
  if (!quote) throw new ChinaPaymentRequestError('alipay_rechange', 'EMPTY', 'No rate or total');
  return quote;
}

/**
 * `getBankList` with `accounttype=ALIPAY` (MYP-61, MYP-110) — the MyPoz accounts a China Payment
 * customer pays into, as raw rows for `normalisePayToAccounts`.
 *
 * Only `accounttype` is sent. Live (2026-10-02) it returns Public Bank, Maybank, DuitNow and
 * Touch 'n Go; adding `currency=RMB` returns none (they are RM accounts), and `bankshowflag` makes
 * no difference. Read-only.
 */
export async function fetchPayToRows(): Promise<readonly unknown[]> {
  const result = await legacyApi.call<LegacyList<unknown>>('getBankList', {
    accounttype: 'ALIPAY',
  });
  if (!result.ok) throw new ChinaPaymentRequestError('getBankList', result.code, result.message);
  return result.data.list;
}

/**
 * Page 1 of `getalipaylist` (MYP-63), newest first — read after a `ChargeResult` call whose answer
 * was lost, to see whether that request was recorded (`findSentCharge`, MYP-109). The raw body;
 * only `findSentCharge` reads it. Read-only.
 */
export async function fetchRecentChargeRows(): Promise<unknown> {
  const result = await legacyApi.call<unknown>('getalipaylist', { page: '1', limit: '10' });
  if (!result.ok) throw new ChinaPaymentRequestError('getalipaylist', result.code, result.message);
  return result.data;
}

/** How a `ChargeResult` call ended. */
export type ChargeOutcome =
  /** Accepted. `orderNo` is the reference to show; undefined if the reply carried none. */
  | { readonly kind: 'submitted'; readonly orderNo?: string }
  /** Refused, with the vendor's own words when it sent any. */
  | { readonly kind: 'refused'; readonly code: string; readonly message: string }
  /**
   * No answer the app can read (network, timeout, an unreadable reply, or one without a code — the
   * old website's own controller answers `status: S` instead): nobody knows if it was recorded.
   */
  | { readonly kind: 'unconfirmed'; readonly code: string };

/**
 * `ChargeResult` (MYP-62, MYP-109) — submits a China Payment request. **A write**: it creates a
 * request staff will process, so it is only ever called from the confirm dialog, once, and never
 * sent again by itself — an unconfirmed answer is checked against the history instead.
 *
 * `params` come from `toChargeResultParams`, which can never carry a password. Never logged here:
 * they hold the customer's name, phone and ID number.
 */
export async function submitCharge(
  params: Readonly<Record<string, string>>,
): Promise<ChargeOutcome> {
  const result = await legacyApi.call<Readonly<Record<string, unknown>>>('ChargeResult', params);
  if (result.ok) {
    const orderNo = chargeOrderNumber(result.data);
    return orderNo ? { kind: 'submitted', orderNo } : { kind: 'submitted' };
  }
  if (isUnconfirmedFailure(result.code) || result.code === 'UNKNOWN') {
    return { kind: 'unconfirmed', code: result.code };
  }
  return { kind: 'refused', code: result.code, message: result.message };
}

/**
 * `getalipaylist` (MYP-63) — one page of the customer's China Payment requests, newest first, as
 * the raw body for `normaliseChinaPaymentRecords`. Read-only.
 *
 * Its envelope is `noCode`: `{data: {count, list}}` with no `code` at all (live, 22 Oct 2026). It
 * timed out on the vendor's side from 28 Sep to 2 Oct (api-issues #26); on 22 Oct it answered in
 * about half a second.
 */
export async function fetchChinaPaymentRecordsPage(page: number, limit: number): Promise<unknown> {
  const result = await legacyApi.call<unknown>('getalipaylist', {
    page: String(page),
    limit: String(limit),
  });
  if (!result.ok) throw new ChinaPaymentRequestError('getalipaylist', result.code, result.message);
  return result.data;
}

/**
 * `alipaydetail` (MYP-63) — one request, as the bare array the vendor answers, for
 * `normaliseChinaPaymentDetail`. An empty array means no such request for this customer. Read-only.
 */
export async function fetchChinaPaymentDetail(id: string): Promise<unknown> {
  const result = await legacyApi.call<unknown>('alipaydetail', { pid: id });
  if (!result.ok) throw new ChinaPaymentRequestError('alipaydetail', result.code, result.message);
  return result.data;
}
